import type { CredentialResponse } from "@react-oauth/google";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useAuth } from "../context/Auth/AuthContext";
import { getUserRecordState } from "../services/auth/accountRecovery";
import { establishGoogleSession, GoogleSession } from "../services/auth/googleSignIn";
import { redirectAfterSignIn } from "../services/auth/postAuthRedirect";
import { clearPendingSignup, getPendingSignup, setPendingSignup } from "../Views/Auth/pendingSignup";

interface UseGoogleSignInOptions {
	accountCheckAttempts?: number;
	accountCheckFallback?: "error" | "existing";
	onSignedIn?: (session: GoogleSession) => "stop" | void;
	onExistingAccount?: (session: GoogleSession) => void;
	onNewAccount?: (session: GoogleSession) => void;
	onAccountCheckFailed?: (session: GoogleSession) => void;
	onError?: () => void;
}

export function useGoogleSignIn({
	accountCheckAttempts = 1,
	accountCheckFallback = "error",
	onSignedIn,
	onExistingAccount,
	onNewAccount,
	onAccountCheckFailed,
	onError,
}: UseGoogleSignInOptions = {}) {
	const navigate = useNavigate();
	const { setSession } = useAuth();
	const publishSession = useCallback(
		(session: GoogleSession) => {
			setSession({ token: session.credential, expiresAt: session.expiresAt, userData: session.profile });
		},
		[setSession],
	);

	const handleGoogleError = useCallback(() => {
		if (onError) onError();
		else toast.error("Login Failed");
	}, [onError]);

	const handleGoogleSignIn = useCallback(
		async (googleUser: CredentialResponse) => {
			const session = establishGoogleSession(googleUser);
			if (!session) {
				handleGoogleError();
				return;
			}
			publishSession(session);

			if (onSignedIn?.(session) === "stop") {
				return;
			}

			const recordState = await getUserRecordState({
				attempts: accountCheckAttempts,
			});

			if (recordState === "unknown" && accountCheckFallback === "error") {
				if (onAccountCheckFailed) onAccountCheckFailed(session);
				else toast.error("We couldn't verify your account. Please try again.");
				return;
			}

			if (recordState !== "missing") {
				clearPendingSignup();
				if (onExistingAccount) onExistingAccount(session);
				else redirectAfterSignIn(session.credential, (to) => navigate(to, { replace: true }));
				return;
			}

			const draft = getPendingSignup();
			const theirs = draft?.email?.trim().toLowerCase() === session.profile.email?.trim().toLowerCase();

			setPendingSignup({
				...(theirs ? draft : undefined),
				firstName: (theirs && draft?.firstName) || session.profile.firstName || "",
				lastName: (theirs && draft?.lastName) || session.profile.lastName || "",
				email: session.profile.email,
				method: "google",
				emailVerified: true,
			});

			if (onNewAccount) {
				onNewAccount(session);
				return;
			}

			navigate("/onboarding", { state: { method: "google" } });
		},
		[
			accountCheckAttempts,
			accountCheckFallback,
			handleGoogleError,
			navigate,
			onAccountCheckFailed,
			onExistingAccount,
			onNewAccount,
			onSignedIn,
			publishSession,
		],
	);

	return { handleGoogleSignIn, handleGoogleError };
}
